import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { CheckCircle, HourglassBottom, Publish } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '../../../shared/api';
import type { Class, ExamType, ResultPublication } from '../../../shared/types';
import PageHeader from '../../../shared/PageHeader';
import { formatDateTime } from '../../../shared/utils';

export default function ResultPublishPage() {
  const qc = useQueryClient();
  const pubsQ = useQuery({ queryKey: ['result-pubs'], queryFn: async () => (await api.get<ResultPublication[]>('/results/publications')).data });
  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const examTypesQ = useQuery({ queryKey: ['exam-types'], queryFn: async () => (await api.get<ExamType[]>('/exam-types')).data });

  const publish = useMutation({
    mutationFn: async (id: string) => (await api.post(`/results/${id}/publish`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['result-pubs'] }),
  });

  const classMap = new Map((classesQ.data ?? []).map((c) => [c.id, `${c.name}-${c.section}`]));
  const examMap = new Map((examTypesQ.data ?? []).map((e) => [e.id, e.name]));

  return (
    <Box>
      <PageHeader title="Result Workflow" subtitle="Review marks submitted by teachers and publish results class-wise." />

      {publish.isError && <Alert severity="error" sx={{ mb: 2 }}>{extractError(publish.error)}</Alert>}
      {publish.isSuccess && <Alert severity="success" sx={{ mb: 2 }}>Result published. Students notified.</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Class</TableCell>
              <TableCell>Exam</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Published at</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(pubsQ.data ?? []).map((rp) => {
              const pct = rp.totalSubjects ? Math.round(rp.submittedSubjects / rp.totalSubjects * 100) : 0;
              return (
                <TableRow key={rp.id}>
                  <TableCell sx={{ fontWeight: 700 }}>{classMap.get(rp.classId) ?? rp.classId}</TableCell>
                  <TableCell>{examMap.get(rp.examTypeId) ?? rp.examTypeId}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 200 }}>
                      <Typography variant="caption">{rp.submittedSubjects}/{rp.totalSubjects}</Typography>
                      <Box sx={{ flex: 1 }}><LinearProgress variant="determinate" value={pct} /></Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {rp.status === 'PUBLISHED' && <Chip color="success" icon={<CheckCircle />} label="Published" size="small" />}
                    {rp.status === 'READY' && <Chip color="warning" label="Ready to publish" size="small" />}
                    {rp.status === 'PENDING' && <Chip icon={<HourglassBottom />} label="Awaiting marks" size="small" />}
                  </TableCell>
                  <TableCell>{rp.publishedAt ? formatDateTime(rp.publishedAt) : '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained" startIcon={<Publish />} size="small"
                      disabled={rp.status !== 'READY' || publish.isPending}
                      onClick={() => publish.mutate(rp.id)}
                    >
                      Publish
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
